import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, FormEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import placeCoordinates from "../place-coordinates.json";
import { distanceTrivia } from "../distance-trivia";
import { sanitizeImage } from "../sanitize-image";
import { readChosenPhoto } from "../chosen-photo";
import { formatDate } from "../postcard-dates";
import { DateField } from "../date-field";
import { acquisitionDetailLabels, acquisitionLabel, acquisitionLabels } from "../acquisition";
import { ordinal, senderKey, senderRuns, toggledSender } from "../sender-runs";
import { alsoNarrowedTo } from "../gallery-filters";
import { awaitingRating } from "../card-ratings";
import type { OwnerRating } from "../card-ratings";
import { OnlyYou, RatingStars, StarPicker, TagChips, TagPicker } from "../rating-controls";
import {
  EMPTY_THREAD, groupLabel, hasThreadDetails, inThread, moderatorOptions, safeThreadLink, threadAutofill, threadKey, threadOptions,
} from "../forum-threads";
import type { OwnerThread } from "../forum-threads";
import { UNITED_STATES, countryFromParam, isUsState, normalizedCountry, normalizedState, stateFromParam, titleCase } from "../places";

type Postcard = {
  id: string;
  photoUrl: string;
  senderUser: string;
  originCountry: string;
  originRegion: string;
  collectionName: string;
  collectionTotal: number | null;
  subjectTags: string[];
  acquisitionType: string;
  acquisitionDetail: string;
  sentDate: string;
  receivedDate: string;
  yearPrinted: number | null;
  message: string;
  isFavorite: boolean;
  isDraft: boolean;
  focalX: number;
  focalY: number;
  showWholeCard: boolean;
  createdAt: string;
  /** The owner's rating. The server sends it to the owner only; a visitor's card has none. */
  ownerRating?: OwnerRating;
  /** Which forum thread it came from. Owner only, like ownerRating. */
  ownerThread?: OwnerThread;
};

/** The "how I got it" that shows the thread fields, and the forum type that shows the moderator. */
const FORUM = "postcrossing-forum";
const ROUND_ROBIN = "round-robin";

/** The thread fields' form names. The server reads these (threadFromForm). */
const THREAD_FIELD_NAMES: Record<keyof OwnerThread, string> = {
  thread: "forumThread",
  group: "forumGroup",
  topic: "forumTopic",
  link: "forumLink",
  moderator: "forumModerator",
};

/** Whether a card's rating has anything to draw: stars, 0, or tags. */
function hasRatingDetails(rating: OwnerRating | undefined) {
  return Boolean(rating && (rating.rating !== null || rating.tags.length > 0));
}

/**
 * The thread under a card's owner-only rule: "WBMP RR · Group 356 · Decorated", the
 * thread name linking out to the forum when it has a link, then the moderator.
 */
function ThreadLine({ thread }: { thread: OwnerThread }) {
  const link = safeThreadLink(thread.link);
  const name = thread.thread || (link ? "Forum thread" : "");
  const details = [groupLabel(thread.group), thread.topic].filter(Boolean);
  return (
    <p className="card-thread">
      {name && (link
        ? <a href={link} target="_blank" rel="noopener noreferrer">{name}<span className="sr-only"> (opens the forum thread)</span></a>
        : <span className="card-thread-name">{name}</span>)}
      {details.map((detail, index) => <span key={index}>{(name || index > 0) && " · "}{detail}</span>)}
      {thread.moderator && <span className="card-thread-moderator">Moderator: {thread.moderator}</span>}
    </p>
  );
}

/** Matches models.CenterFocus: an uncropped card sits in the middle of its block. */
const CENTER_FOCUS = 50;

/**
 * Widths the image endpoint will generate. Offering all three and letting the browser
 * choose is what makes a phone download a 320px card instead of a 2000px one.
 */
const photoSrcSet = (url: string) => `${url}?w=320 320w, ${url}?w=640 640w, ${url}?w=1024 1024w`;

const suggestedSubjectTags = [
  "illustrated", "landscape", "city view", "architecture", "map", "flag",
  "animal", "cat", "dog", "bird", "pig", "flowers", "nature", "food",
  "space", "art", "vintage", "holiday", "transportation", "people",
];

// Countries that send the most Postcrossing mail, spelled the way the server stores
// them, so the Country field can suggest a spelling before a new one gets invented.
// Countries already in the collection are merged in, so this list only has to cover
// likely first-time senders.
const frequentSenderCountries = [
  "Germany", "Russia", "United States", "Netherlands", "Finland", "China", "Taiwan",
  "Belarus", "Czechia", "Japan", "Poland", "France", "United Kingdom", "Ukraine",
  "Canada", "Lithuania", "Latvia", "Italy", "Spain", "Austria", "Switzerland",
  "India", "Belgium", "Ireland", "Denmark", "Sweden", "Norway", "Portugal",
  "Estonia", "Hungary", "Slovakia", "Slovenia", "Croatia", "Greece", "Turkey",
  "South Korea", "Hong Kong", "Singapore", "Malaysia", "Thailand", "Indonesia",
  "Philippines", "Vietnam", "Australia", "New Zealand", "Brazil", "Mexico",
];

type Coordinate = [longitude: number, latitude: number];

const AURORA: Coordinate = [-104.8319, 39.7294];

// Centroids generated at build time from the same atlases the maps page draws
// (`npm run generate:coordinates`), so this page no longer parses 217 KB of polygons
// to look up ~250 points. The generator already applies places.ts's precedence —
// hand-placed override, then atlas centroid, then the table for places with no shape —
// so a plain lookup here is the whole countryCoordinate chain.
const countryCoordinates = new Map(Object.entries(placeCoordinates.countries) as [string, Coordinate][]);
const stateCoordinates = new Map(Object.entries(placeCoordinates.states) as [string, Coordinate][]);

function milesBetween([longitudeA, latitudeA]: Coordinate, [longitudeB, latitudeB]: Coordinate) {
  const radians = Math.PI / 180;
  const latitudeDelta = (latitudeB - latitudeA) * radians;
  const longitudeDelta = (longitudeB - longitudeA) * radians;
  const a = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA * radians) * Math.cos(latitudeB * radians) * Math.sin(longitudeDelta / 2) ** 2;
  return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function postcardDistance(postcard: Postcard) {
  // Same key as the map buckets and the gallery filters — a name that folds onto a legacy
  // shim key reaches no coordinate under normalizedCountry, and the card travels zero miles.
  const country = countryFromParam(postcard.originCountry);
  if (country === UNITED_STATES) {
    const state = stateCoordinates.get(normalizedState(postcard.originRegion));
    if (state) return milesBetween(state, AURORA);
  }
  const origin = countryCoordinates.get(country);
  return origin ? milesBetween(origin, AURORA) : 0;
}

function compactNumber(value: number) {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(Math.round(value));
}

function dayNumber(date: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year, month - 1, day) / 86_400_000;
}

function travelDays(postcard: Postcard) {
  const sent = dayNumber(postcard.sentDate);
  const received = dayNumber(postcard.receivedDate);
  if (sent === null || received === null) return null;
  return Math.max(0, received - sent);
}

/** How long each distance comparison holds before the next one takes its place. */
const TRIVIA_INTERVAL_MS = 4500;

/**
 * Cycles through the distance comparisons, because one line cannot carry a number this
 * big and the collection has earned a dozen different ways of saying it.
 *
 * A reader who has asked for less motion gets the first line and no cycling at all —
 * the comparisons are ordered so the first is the one most worth seeing.
 */
function RotatingDetail({ lines }: { lines: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (lines.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % lines.length);
    }, TRIVIA_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [lines]);

  // A shorter list can arrive mid-cycle when a postcard is added, so never trust the index.
  const line = lines[index] ?? lines[0] ?? "";
  // Keying on the text restarts the fade, so each comparison arrives rather than blinks.
  return <span key={line} className="stat-trivia">{line}</span>;
}

function Stat({ value, label, detail }: { value: string | number; label: string; detail: ReactNode }) {
  return (
    <article className="stat-card">
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </article>
  );
}

export default function HomePage() {
  const [postcards, setPostcards] = useState<Postcard[]>([]);
  const [drafts, setDrafts] = useState<Postcard[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("received-newest");
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [acquisitionFilter, setAcquisitionFilter] = useState("all");
  // The sender's own spelling, as it appears on the card that was tapped, so the chip can
  // show it back; every comparison goes through senderKey.
  const [senderFilter, setSenderFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Postcard | null>(null);
  const [photoPreview, setPhotoPreview] = useState("");
  // The in-memory copy of the photo just picked, which is what gets uploaded rather than
  // the file input's own reference; see readChosenPhoto. Resolves to null if the read failed.
  const chosenPhoto = useRef<Promise<File | null> | null>(null);
  const [notice, setNotice] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  // Where each sent card went (#56): the only part of them anyone else sees.
  const [sentPlaces, setSentPlaces] = useState<{ country: string; region: string }[]>([]);
  const [sentWaiting, setSentWaiting] = useState(0);
  const [taggingCardId, setTaggingCardId] = useState("");
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [customTags, setCustomTags] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  const [formSubjectTags, setFormSubjectTags] = useState("");
  const [focalX, setFocalX] = useState(CENTER_FOCUS);
  const [focalY, setFocalY] = useState(CENTER_FOCUS);
  const [showWholeCard, setShowWholeCard] = useState(false);
  // Controlled, unlike the rest of the form, because the picker has to write into them.
  const [sentDate, setSentDate] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  // The optional rating at the foot of the form. null is unrated, which is not 0.
  const [formRating, setFormRating] = useState<number | null>(null);
  const [formRatingTags, setFormRatingTags] = useState<string[]>([]);
  const [ratingTagsOpen, setRatingTagsOpen] = useState(false);
  // Controlled because they decide which thread fields show.
  const [formAcquisitionType, setFormAcquisitionType] = useState("");
  const [formAcquisitionDetail, setFormAcquisitionDetail] = useState("");
  // The five thread fields, controlled because auto-fill writes into them.
  const [formThread, setFormThread] = useState<OwnerThread>(EMPTY_THREAD);
  const [threadFilledFrom, setThreadFilledFrom] = useState("");
  // Owner only: a threadKey, or "all".
  const [threadFilter, setThreadFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedCard = useRef("");
  const senderChip = useRef<HTMLDivElement>(null);

  // The maps link here with ?country= or ?region=. Normalising the parameter through the
  // same helpers the maps use is what keeps "Germany · 5" on the map and five cards here.
  const countryParam = searchParams.get("country");
  const regionParam = searchParams.get("region");
  const placeKind = countryParam ? "country" : regionParam ? "region" : "";
  const placeValue = countryParam
    ? countryFromParam(countryParam)
    : regionParam ? stateFromParam(regionParam) : "";

  function clearPlaceFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("country");
    next.delete("region");
    setSearchParams(next, { replace: true });
  }

  async function refresh() {
    const response = await fetch("/api/postcards", { cache: "no-store" });
    if (!response.ok) throw new Error("Could not load postcards");
    const payload = await response.json() as { postcards: Postcard[] };
    setPostcards(payload.postcards);
    setLoaded(true);
  }

  async function refreshDrafts() {
    const response = await fetch("/api/postcards/drafts", { cache: "no-store" });
    if (!response.ok) throw new Error("Could not load new postcards");
    const payload = await response.json() as { postcards: Postcard[] };
    setDrafts(payload.postcards);
  }

  useEffect(() => {
    // refresh() awaits fetch() before any setState, and .catch() runs in a microtask,
    // so nothing here sets state synchronously — the rule can't see through the promise.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    refresh().catch(() => {
      setNotice("This browser could not open your postcard collection.");
      setLoaded(true);
    });
    fetch("/api/me", { cache: "no-store" })
      .then((response) => response.json())
      .then((viewer: { signedIn: boolean; isOwner: boolean }) => {
        setSignedIn(viewer.signedIn);
        setIsOwner(viewer.isOwner);
        if (viewer.isOwner) refreshDrafts().catch(() => setNotice("The new-postcard queue could not be opened."));
        if (viewer.isOwner) {
          fetch("/api/sent", { cache: "no-store" })
            .then((response) => response.json())
            .then((payload: { mailings?: { isDraft: boolean }[] }) => setSentWaiting((payload.mailings ?? []).filter((mailing) => mailing.isDraft).length))
            .catch(() => undefined);
        }
      })
      .catch(() => undefined);
    fetch("/api/sent/places", { cache: "no-store" })
      .then((response) => response.json())
      .then((payload: { places?: { country: string; region: string }[] }) => setSentPlaces(payload.places ?? []))
      .catch(() => undefined);
  }, []);

  // A drawer thumbnail links to /?country=germany#card-<id>. Once the filtered gallery has
  // rendered, bring that card into view and mark it, so the tap lands on the actual card
  // rather than the top of a filtered page.
  useEffect(() => {
    const target = window.location.hash.replace("#card-", "");
    if (!target || !loaded || highlightedCard.current === target) return;
    const node = document.getElementById(`card-${target}`);
    if (!node) return;
    highlightedCard.current = target;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    node.classList.add("just-arrived");
    const timer = window.setTimeout(() => node.classList.remove("just-arrived"), 2600);
    return () => window.clearTimeout(timer);
  }, [loaded, postcards]);

  // A tap on a name or postmark far down the page shrinks the gallery to that sender's
  // cards and leaves the page near its new bottom, with the "Postcards from" chip off the
  // top of the screen. Bring the chip up to just under the sticky header, so the first of
  // their cards and the way back are both in view (#74). Measured rather than a fixed
  // scroll-margin: the header wraps onto a second line on a phone.
  useEffect(() => {
    const chip = senderChip.current;
    if (!senderFilter || !chip) return;
    const header = document.querySelector(".topbar")?.getBoundingClientRect().bottom ?? 0;
    window.scrollTo({ top: chip.getBoundingClientRect().top + window.scrollY - header - 12, behavior: "smooth" });
  }, [senderFilter]);

  const collections = useMemo(
    () => [...new Set(postcards.map((card) => card.collectionName).filter(Boolean))].sort(),
    [postcards],
  );

  const untaggedCards = useMemo(
    () => postcards.filter((card) => card.subjectTags.length === 0),
    [postcards],
  );
  const allSuggestedTags = useMemo(
    () => [...new Set([...suggestedSubjectTags, ...postcards.flatMap((card) => card.subjectTags)])]
      .sort((a, b) => a.localeCompare(b)),
    [postcards],
  );
  const taggingCard = postcards.find((card) => card.id === taggingCardId) ?? null;

  const knownCountries = useMemo(
    () => [...new Set([...frequentSenderCountries, ...postcards.map((card) => card.originCountry).filter(Boolean)])]
      .sort((a, b) => a.localeCompare(b)),
    [postcards],
  );

  // Every card of hers, drafts too, is where the thread suggestions and auto-fill come
  // from. A visitor's cards carry no ownerThread, so for them all of this is empty.
  const threadSource = useMemo(() => [...postcards, ...drafts], [postcards, drafts]);
  const knownThreads = useMemo(() => threadOptions(threadSource), [threadSource]);
  const knownModerators = useMemo(() => moderatorOptions(threadSource), [threadSource]);
  // The gallery filter offers only published cards' threads, since those are what it lists.
  const galleryThreads = useMemo(() => threadOptions(postcards), [postcards]);
  // What else is narrowing the list while it is filtered to one sender, for that sender's bar.
  const senderNarrowedTo = alsoNarrowedTo(
    { query, collection: collectionFilter, acquisition: acquisitionFilter, thread: threadFilter },
    galleryThreads,
  );

  const collectionCounts = useMemo(() => {
    const counts = new Map<string, { owned: number; total: number | null }>();
    postcards.forEach((card) => {
      if (!card.collectionName) return;
      const current = counts.get(card.collectionName) ?? { owned: 0, total: card.collectionTotal };
      current.owned += 1;
      if (card.collectionTotal) current.total = card.collectionTotal;
      counts.set(card.collectionName, current);
    });
    return counts;
  }, [postcards]);

  // Keyed by card id, and only for senders who have written more than once — a card that
  // is absent from this map wears no postmark.
  const runs = useMemo(() => senderRuns(postcards), [postcards]);

  // The quick-rate list's length, for the owner's header. Always 0 for a visitor, whose
  // cards carry no rating at all.
  const waitingToRate = useMemo(() => awaitingRating(postcards).length, [postcards]);

  const visibleCards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const favoritesOnly = needle === "favorite" || needle === "favorites";
    const filtered = postcards.filter((card) => {
      const searchable = [
        card.senderUser,
        card.originCountry,
        card.originRegion,
        card.collectionName,
        card.message,
        acquisitionLabel(card),
        ...card.subjectTags,
        // Only the owner's cards have these, so a visitor's search cannot match on them.
        card.ownerThread?.thread ?? "",
        card.ownerThread?.group ?? "",
        card.ownerThread?.topic ?? "",
      ].join(" ").toLowerCase();
      // countryFromParam on both sides, not normalizedCountry: placeValue has been through
      // the legacy-link shim, and a stored name can fold onto one of the keys that shim
      // answers for — "Trkiye" folds to the key the pre-fold link for Türkiye used. Running
      // the same function on the card is what keeps it matching the link it minted. The
      // region branch compares against a constant rather than a parameter, so it does not
      // need the shim.
      const matchesPlace = !placeKind || (placeKind === "country"
        ? countryFromParam(card.originCountry) === placeValue
        : normalizedCountry(card.originCountry) === UNITED_STATES &&
          normalizedState(card.originRegion) === placeValue);
      return (!needle || (favoritesOnly ? card.isFavorite : searchable.includes(needle))) &&
        matchesPlace &&
        // An exact match on the folded name, not the search box: a substring search for
        // "ana" would also answer with Ana-Maria's cards, and a postmark that promises
        // four and shows seven is worse than no postmark.
        (!senderFilter || senderKey(card.senderUser) === senderKey(senderFilter)) &&
        (collectionFilter === "all" || card.collectionName === collectionFilter) &&
        (acquisitionFilter === "all" || card.acquisitionType === acquisitionFilter) &&
        (threadFilter === "all" || inThread(card, threadFilter));
    });

    // Both date sorts break a tie on created_at, in the same direction they are sorting.
    // Without it they lean on the order the server happened to send — which is
    // `received_date DESC, created_at DESC` — and a stable sort then leaves a pair received
    // on one day newest-entered first even while the page ascends, so the run postmarks on
    // that pair read 4th above 3rd. senderRuns numbers a run by the same two fields, and
    // these are what keep its numbers agreeing with the order they are read in.
    return filtered.sort((a, b) => {
      if (sortBy === "received-oldest") {
        return a.receivedDate.localeCompare(b.receivedDate) || a.createdAt.localeCompare(b.createdAt);
      }
      if (sortBy === "sender") return a.senderUser.localeCompare(b.senderUser);
      if (sortBy === "country") return a.originCountry.localeCompare(b.originCountry);
      if (sortBy === "travel") return (travelDays(b) ?? -1) - (travelDays(a) ?? -1);
      if (sortBy === "collection") return a.collectionName.localeCompare(b.collectionName);
      return b.receivedDate.localeCompare(a.receivedDate) || b.createdAt.localeCompare(a.createdAt);
    });
  }, [postcards, query, sortBy, collectionFilter, acquisitionFilter, threadFilter, senderFilter, placeKind, placeValue]);

  // Defining the countries using the normalized country name. This ensures the count matches the map's
  // country count. Without this, it would count countries like "United Kingdom" and "England" as separate countries.
  // This is not limited to what we shade on the map: Macao is counted, but the map does not have an area large enough
  // to shade it.
  const countries = new Set(postcards.map((card) => countryFromParam(card.originCountry))).size;
  // US states only, counted the way the map shades them. This stat once counted every
  // region worldwide — Quebec permanently, and briefly a Spanish town — so the number
  // could never be reconciled against the map, and a new state appearing to not count
  // read as a bug. Only the fifty states and DC count — the shapes the state map draws —
  // so a card from "NYC" or "Puerto Rico" cannot bump the number while shading nothing.
  const states = new Set(
    postcards
      .filter((card) => normalizedCountry(card.originCountry) === UNITED_STATES && isUsState(card.originRegion))
      .map((card) => normalizedState(card.originRegion)),
  ).size;
  // senderKey, not a bare toLowerCase: this number and the postmarks have to be counting
  // the same people, or a name typed with a stray space shows a sender here whose cards
  // carry no run.
  const senders = new Set(postcards.map((card) => senderKey(card.senderUser))).size;
  const sentCountries = new Set(sentPlaces.map((place) => countryFromParam(place.country)).filter(Boolean)).size;
  const trips = postcards.map(travelDays).filter((days): days is number => days !== null);
  const averageTravel = trips.length
    ? `${Math.round(trips.reduce((sum, days) => sum + days, 0) / trips.length)}d`
    : "—";
  const totalDistanceMiles = postcards.reduce((sum, postcard) => sum + postcardDistance(postcard), 0);
  const distanceValue = totalDistanceMiles ? `≈${compactNumber(totalDistanceMiles)} mi` : "—";
  // Memoized on the total rather than the postcards: the same miles must hand back the
  // same array, or the rotation restarts on every render and never gets past line one.
  const distanceLines = useMemo(() => distanceTrivia(totalDistanceMiles), [totalDistanceMiles]);

  function openAddForm() {
    setEditing(null);
    setFormSubjectTags("");
    setPhotoPreview("");
    chosenPhoto.current = null;
    setFocalX(CENTER_FOCUS);
    setFocalY(CENTER_FOCUS);
    setShowWholeCard(false);
    setSentDate("");
    setReceivedDate("");
    setFormRating(null);
    setFormRatingTags([]);
    setRatingTagsOpen(false);
    setFormAcquisitionType("");
    setFormAcquisitionDetail("");
    setFormThread(EMPTY_THREAD);
    setThreadFilledFrom("");
    setShowForm(true);
  }

  function openEditForm(card: Postcard) {
    setEditing(card);
    setFormSubjectTags(card.subjectTags.join(", "));
    setPhotoPreview(card.photoUrl);
    chosenPhoto.current = null;
    setFocalX(card.focalX);
    setFocalY(card.focalY);
    setShowWholeCard(card.showWholeCard);
    setSentDate(card.sentDate);
    setReceivedDate(card.receivedDate);
    setFormRating(card.ownerRating?.rating ?? null);
    setFormRatingTags(card.ownerRating?.tags ?? []);
    // Open already when there are tags to show, so an edit never hides what is saved.
    setRatingTagsOpen(Boolean(card.ownerRating?.tags.length));
    setFormAcquisitionType(card.acquisitionType);
    setFormAcquisitionDetail(card.acquisitionDetail);
    setFormThread(card.ownerThread ?? EMPTY_THREAD);
    setThreadFilledFrom("");
    setShowForm(true);
  }

  function openDraftReview() {
    if (drafts[0]) openEditForm(drafts[0]);
  }

  function closeForm() {
    if (photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setShowForm(false);
    setEditing(null);
    setPhotoPreview("");
    chosenPhoto.current = null;
    setFormSubjectTags("");
    setFocalX(CENTER_FOCUS);
    setFocalY(CENTER_FOCUS);
    setShowWholeCard(false);
    setSentDate("");
    setReceivedDate("");
    setFormRating(null);
    setFormRatingTags([]);
    setRatingTagsOpen(false);
    setFormAcquisitionType("");
    setFormAcquisitionDetail("");
    setFormThread(EMPTY_THREAD);
    setThreadFilledFrom("");
  }

  /**
   * Fills the link and moderator from her last card in the thread just entered, into
   * whichever of the two is still empty (threadAutofill). Runs when she leaves the box or
   * picks a suggestion, not on every keystroke, where "WBMP" on the way to "WBMP RR" could
   * match some other thread and fill boxes she would then have to empty.
   */
  function fillFromThread(name: string) {
    const filled = threadAutofill(threadSource, name, formThread, editing?.id);
    if (filled.link === formThread.link && filled.moderator === formThread.moderator) return;
    setFormThread((current) => ({ ...current, ...filled }));
    setThreadFilledFrom(knownThreads.find((option) => option.key === threadKey(name))?.label ?? name.trim());
  }

  function changeThreadName(event: ChangeEvent<HTMLInputElement>) {
    const name = event.target.value;
    setFormThread((current) => ({ ...current, thread: name }));
    setThreadFilledFrom("");
    // Picking a suggestion arrives as a change with no typing behind it: Chrome calls it
    // insertReplacementText, and some browsers send a plain Event.
    const native = event.nativeEvent;
    if (!(native instanceof InputEvent) || native.inputType === "insertReplacementText") fillFromThread(name);
  }

  function toggleFormRatingTag(key: string) {
    setFormRatingTags((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  }

  function toggleFormSubjectTag(tag: string) {
    setFormSubjectTags((current) => {
      const tags = current.split(",").map((item) => item.trim().toLowerCase()).filter(Boolean);
      const next = tags.includes(tag) ? tags.filter((item) => item !== tag) : [...tags, tag];
      return next.join(", ");
    });
  }

  function handlePhotoChange(input: HTMLInputElement) {
    const file = input.files?.[0];
    if (!file) return;
    // Every callback below checks it is still the current pick: a second photo chosen, or
    // the form closed, while this one was reading has taken over.
    const photo: Promise<File | null> = readChosenPhoto(file).catch((error: Error) => {
      if (chosenPhoto.current === photo) {
        // No photo is chosen any more, so a later Save of the other fields goes ahead and
        // keeps the card's photo. A Save already waiting on this read holds its own
        // reference to it, and still stops.
        chosenPhoto.current = null;
        // Emptying the input lets the same photo be chosen again straight away, and lets
        // `required` stop an Add pressed before it is.
        input.value = "";
        if (photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
        // Back to the photo the card already has, and that photo's crop: an earlier pick
        // that did succeed recentred the focal point, which Save would otherwise write onto
        // the old photo.
        setPhotoPreview(editing?.photoUrl ?? "");
        setFocalX(editing?.focalX ?? CENTER_FOCUS);
        setFocalY(editing?.focalY ?? CENTER_FOCUS);
        setNotice(error.message);
      }
      return null;
    });
    chosenPhoto.current = photo;
    void photo.then((copy) => {
      if (!copy || chosenPhoto.current !== photo) return;
      if (photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
      // Drawn from the same in-memory copy that will be uploaded, so what the preview
      // shows is exactly what gets added.
      setPhotoPreview(URL.createObjectURL(copy));
      // The old focal point belonged to the old photo.
      setFocalX(CENTER_FOCUS);
      setFocalY(CENTER_FOCUS);
    });
  }

  function pickFocalPoint(event: ReactMouseEvent<HTMLButtonElement>) {
    // Enter and Space on a button fire a click with no coordinates, which would otherwise
    // read as a tap on the top-left corner. Those keys are the arrow keys' job below.
    if (event.detail === 0) return;
    const box = event.currentTarget.getBoundingClientRect();
    setFocalX(Math.round(((event.clientX - box.left) / box.width) * 100));
    setFocalY(Math.round(((event.clientY - box.top) / box.height) * 100));
  }

  // Arrow keys nudge the point, so choosing a crop never requires a steady tap.
  function nudgeFocalPoint(event: ReactKeyboardEvent<HTMLButtonElement>) {
    const steps: Record<string, [number, number]> = {
      ArrowLeft: [-2, 0], ArrowRight: [2, 0], ArrowUp: [0, -2], ArrowDown: [0, 2],
    };
    const step = steps[event.key];
    if (!step) return;
    event.preventDefault();
    setFocalX((current) => Math.min(100, Math.max(0, current + step[0])));
    setFocalY((current) => Math.min(100, Math.max(0, current + step[1])));
  }

  function openTagReview(card = untaggedCards[0]) {
    if (!card) return;
    setTaggingCardId(card.id);
    setReviewTags(card.subjectTags);
    setCustomTags("");
  }

  function advanceTagReview() {
    if (!taggingCard) return;
    const currentIndex = untaggedCards.findIndex((card) => card.id === taggingCard.id);
    const next = untaggedCards[currentIndex + 1] ?? untaggedCards.find((card) => card.id !== taggingCard.id);
    if (next) openTagReview(next);
    else setTaggingCardId("");
  }

  function toggleReviewTag(tag: string) {
    setReviewTags((current) => current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]);
  }

  async function saveReviewTags(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!taggingCard) return;
    const typedTags = customTags.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean);
    const tags = [...new Set([...reviewTags, ...typedTags])];
    if (tags.length === 0) {
      setNotice("Choose or type at least one subject tag.");
      return;
    }

    setSavingTags(true);
    const data = new FormData();
    data.set("senderUser", taggingCard.senderUser);
    data.set("originCountry", taggingCard.originCountry);
    data.set("originRegion", taggingCard.originRegion);
    data.set("collectionName", taggingCard.collectionName);
    if (taggingCard.collectionTotal !== null) data.set("collectionTotal", String(taggingCard.collectionTotal));
    data.set("subjectTags", tags.join(", "));
    data.set("acquisitionType", taggingCard.acquisitionType);
    data.set("acquisitionDetail", taggingCard.acquisitionDetail);
    data.set("sentDate", taggingCard.sentDate);
    data.set("receivedDate", taggingCard.receivedDate);
    if (taggingCard.yearPrinted !== null) data.set("yearPrinted", String(taggingCard.yearPrinted));
    data.set("message", taggingCard.message);
    if (taggingCard.isFavorite) data.set("isFavorite", "on");
    data.set("focalX", String(taggingCard.focalX));
    data.set("focalY", String(taggingCard.focalY));
    if (taggingCard.showWholeCard) data.set("showWholeCard", "on");

    const response = await fetch(`/api/postcards/${taggingCard.id}`, { method: "PUT", body: data });
    if (!response.ok) {
      setNotice("Could not save those tags.");
      setSavingTags(false);
      return;
    }
    const nextIndex = untaggedCards.findIndex((card) => card.id === taggingCard.id) + 1;
    const next = untaggedCards[nextIndex] ?? untaggedCards.find((card) => card.id !== taggingCard.id) ?? null;
    await refresh();
    if (next) openTagReview(next);
    else {
      setTaggingCardId("");
      setNotice("All imported postcards have subject tags!");
    }
    setSavingTags(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    // Never the input's own file: on Android that reference may no longer be readable by
    // the time Add is pressed. Only the copy taken at pick time is sent.
    data.delete("photo");
    const wasDraft = Boolean(editing?.isDraft);
    // Before the wait for the photo, not after: `saving` is what disables the button, and a
    // second tap while the copy is still being read would otherwise add the card twice.
    setSaving(true);
    try {
      const pick = chosenPhoto.current;
      const selectedPhoto = pick ? await pick : null;
      // A photo was chosen but could not be read. That has already been said and the input
      // emptied; carrying on would save without the new photo and then report success.
      if (pick && !selectedPhoto) return;
      if (!editing && !selectedPhoto) {
        setNotice("Choose a photo of your postcard.");
        return;
      }
      if (selectedPhoto) {
        setNotice("Removing private metadata from the photo…");
        // Without this catch a phone that cannot decode the photo left the notice reading
        // "Removing private metadata…" for good, with no error and no way to retry.
        const cleanPhoto = await sanitizeImage(selectedPhoto, selectedPhoto.name);
        data.set("photo", cleanPhoto, cleanPhoto.name);
      }
      const response = await fetch(editing ? `/api/postcards/${editing.id}` : "/api/postcards", {
        method: editing ? "PUT" : "POST",
        body: data,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({ error: "Could not save postcard" }));
        setNotice(payload.error ?? "Could not save postcard");
        return;
      }
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not save postcard.");
      return;
    } finally {
      setSaving(false);
    }
    if (wasDraft) {
      const remaining = drafts.filter((card) => card.id !== editing?.id);
      setDrafts(remaining);
      if (remaining[0]) {
        // openEditForm rather than a second copy of its body: it already sets every piece
        // of form state from a card, and the copy here was one field behind the moment the
        // date fields became state.
        openEditForm(remaining[0]);
        setNotice(`Saved. ${remaining.length} new postcard${remaining.length === 1 ? "" : "s"} left.`);
      } else {
        closeForm();
        setNotice("All new postcards are now in your collection!");
      }
      return;
    }
    closeForm();
    setNotice(editing ? "Postcard updated." : "Postcard added to your collection.");
  }

  async function handleDelete(card: Postcard) {
    if (!window.confirm(`Remove the postcard from ${card.senderUser}? This cannot be undone.`)) return;
    const response = await fetch(`/api/postcards/${card.id}`, { method: "DELETE" });
    if (!response.ok) {
      setNotice("Could not remove that postcard.");
      return;
    }
    await refresh();
    setNotice("Postcard removed.");
  }

  async function toggleFavorite(card: Postcard) {
    const nextFavorite = !card.isFavorite;
    setPostcards((current) => current.map((item) => item.id === card.id ? { ...item, isFavorite: nextFavorite } : item));
    const response = await fetch(`/api/postcards/${card.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ isFavorite: nextFavorite }),
    });
    if (!response.ok) {
      setPostcards((current) => current.map((item) => item.id === card.id ? { ...item, isFavorite: card.isFavorite } : item));
      setNotice("Could not update that favorite.");
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="My Collection of Connection home">
          <span className="brand-mark">✉</span>
          <span>My Collection of Connection</span>
        </a>
        <a className="map-link" href="/maps" aria-label="Explore the maps">
          {/* The words are dropped on a narrow screen, not the link — hiding the link
              outright left a phone with no way to reach the maps at all. */}
          <svg className="map-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9" />
            <ellipse cx="12" cy="12" rx="4" ry="9" />
            <line x1="3" y1="12" x2="21" y2="12" />
          </svg>
          <span className="map-link-words">Explore the maps</span>
        </a>
        {isOwner ? (
          <div className="owner-actions">
            {drafts.length > 0 && <button className="secondary-button" onClick={openDraftReview}>Complete {drafts.length} new</button>}
            {untaggedCards.length > 0 && <button className="secondary-button" onClick={() => openTagReview()}>Tag {untaggedCards.length} untagged</button>}
            <a className="ratings-link" href="/ratings" aria-label={waitingToRate ? `Ratings, ${waitingToRate} to rate` : "Ratings"}>
              Ratings{waitingToRate > 0 && <span className="badge-count" aria-hidden="true">{waitingToRate}</span>}
            </a>
            <a className="ratings-link" href="/sent-cards" aria-label={sentWaiting ? `Sent mailings, ${sentWaiting} to check` : "Sent mailings"}>
              Sent mailings{sentWaiting > 0 && <span className="badge-count" aria-hidden="true">{sentWaiting}</span>}
            </a>
            <button className="primary-button" onClick={openAddForm}>＋ Add postcard</button>
          </div>
        ) : (
          signedIn ? (
            // POST, not a link: a third-party <img src="/api/auth/signout"> could otherwise
            // sign the owner out.
            <form method="post" action="/api/auth/signout">
              <button className="owner-link" type="submit">Sign out</button>
            </form>
          ) : (
            <a className="owner-link" href="/signin">Owner sign in</a>
          )
        )}
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow">Sent with care · kept with love</p>
          <h1>My collection<br /><em>of connection</em></h1>
          <p className="hero-copy">A handwritten archive of the people and places that found their way to my mailbox—and the small, beautiful proof that we thought of one another.</p>
        </div>
        <a className="postage-stamp" href="https://www.postcrossing.com/user/Jostockto" target="_blank" rel="noreferrer" aria-label="Visit jostockto’s Postcrossing profile">
          <div className="stamp-inner">
            <small>POSTCROSSING</small>
            <img src="/jostockto-profile.jpg" alt="jostockto’s Postcrossing profile photo" />
            <strong>jostockto</strong>
          </div>
        </a>
      </section>

      <section className={`stats${sentPlaces.length > 0 ? " has-sent" : ""}`} aria-label="Collection statistics">
        <Stat value={postcards.length} label="Postcards" detail="pieces of connection" />
        {sentPlaces.length > 0 && (
          <Stat value={sentPlaces.length} label="Mailings sent" detail={`to ${sentCountries} ${sentCountries === 1 ? "country" : "countries"}`} />
        )}
        <Stat value={countries} label="Countries" detail="represented" />
        <Stat value={states} label="US states" detail="represented" />
        <Stat value={senders} label="Senders" detail="people who reached out" />
        <Stat value={averageTravel} label="Avg. journey" detail="from sent to received" />
        <Stat
          value={distanceValue}
          label="Distance traveled"
          detail={distanceLines.length ? <RotatingDetail lines={distanceLines} /> : "estimated to Aurora"}
        />
      </section>

      <section className="map-invite">
        <div>
          <p className="eyebrow">Every place, on a map</p>
          <h2>See where they all came from</h2>
          <p>
            {countries} {countries === 1 ? "country" : "countries"} and {states} states, shaded by how many
            arrived. Choose any place to see the postcards that came from it.
          </p>
        </div>
        <a className="primary-button" href="/maps">Explore the maps</a>
      </section>

      <section className="archive">
        <div className="section-heading">
          <div><p className="eyebrow">People & places, kept close</p><h2>The connection archive</h2></div>
          <p>{visibleCards.length} {visibleCards.length === 1 ? "card" : "cards"} showing</p>
        </div>

        <div className={`toolbar${isOwner && galleryThreads.length > 0 ? " has-thread-filter" : ""}`}>
          <label className="search-box">
            <span aria-hidden="true">⌕</span>
            <span className="sr-only">Search postcards</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search sender, place, subject…" />
          </label>
          <label>
            <span className="sr-only">Filter by collection</span>
            <select value={collectionFilter} onChange={(event) => setCollectionFilter(event.target.value)}>
              <option value="all">All collections</option>
              {collections.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">Filter by how the postcard was received</span>
            <select value={acquisitionFilter} onChange={(event) => setAcquisitionFilter(event.target.value)}>
              <option value="all">All ways received</option>
              {Object.entries(acquisitionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {/* Owner only: a visitor's cards have no threads to list. */}
          {isOwner && galleryThreads.length > 0 && (
            <label className="thread-filter">
              <span className="sr-only">Filter by forum thread (only you see this)</span>
              <select value={threadFilter} onChange={(event) => setThreadFilter(event.target.value)}>
                <option value="all">All threads</option>
                {galleryThreads.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </label>
          )}
          <label>
            <span className="sr-only">Sort postcards</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="received-newest">Newest received</option>
              <option value="received-oldest">Oldest received</option>
              <option value="sender">Sender A–Z</option>
              <option value="country">Country A–Z</option>
              <option value="travel">Longest journey</option>
              <option value="collection">Collection A–Z</option>
            </select>
          </label>
          {(query || collectionFilter !== "all" || acquisitionFilter !== "all" || threadFilter !== "all" || senderFilter) && (
            <button className="text-button" onClick={() => { setQuery(""); setCollectionFilter("all"); setAcquisitionFilter("all"); setThreadFilter("all"); setSenderFilter(""); }}>Clear</button>
          )}
        </div>

        {placeKind && (
          <div className="place-chip">
            <span>Postcards from <strong>{titleCase(placeValue)}</strong></span>
            <button type="button" onClick={clearPlaceFilter} aria-label={`Stop showing only postcards from ${titleCase(placeValue)}`}>Show all ×</button>
            <a className="back-to-map" href="/maps">← Back to the maps</a>
          </div>
        )}

        {senderFilter && (
          <div className="place-chip" ref={senderChip}>
            <span>Postcards from <strong>{senderFilter}</strong>{senderNarrowedTo && ` · ${senderNarrowedTo}`}</span>
            <button type="button" onClick={() => setSenderFilter("")} aria-label={`Stop showing only postcards from ${senderFilter}`}>Show all ×</button>
          </div>
        )}

        {!loaded ? (
          <div className="empty-state"><div className="empty-stamp">···</div><h3>Opening your collection…</h3></div>
        ) : visibleCards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-stamp">✦</div>
            <h3>{placeKind ? `No postcards from ${titleCase(placeValue)} yet` : postcards.length ? "No connections match that search" : "The first connection starts here"}</h3>
            <p>{postcards.length ? "Try another sender, place, subject, or collection." : "Add a postcard and the person, place, and journey behind it will become part of the archive."}</p>
            {!postcards.length && isOwner && <button className="primary-button" onClick={openAddForm}>Add your first postcard</button>}
          </div>
        ) : (
          <div className="postcard-grid">
            {visibleCards.map((card) => {
              const days = travelDays(card);
              const progress = card.collectionName ? collectionCounts.get(card.collectionName) : null;
              const run = runs.get(card.id);
              // Whether this card's sender is the one the gallery is filtered to, which the
              // postmark and the name both show as pressed and both undo on a tap.
              const filteredToSender = Boolean(senderFilter) && senderKey(senderFilter) === senderKey(card.senderUser);
              return (
                <article className="postcard-card" key={card.id} id={`card-${card.id}`}>
                  <button className={`photo-button${card.showWholeCard ? " is-whole" : ""}`} onClick={() => isOwner && openEditForm(card)} aria-label={isOwner ? `Edit postcard from ${card.senderUser}` : `Postcard from ${card.senderUser}`} disabled={!isOwner}>
                    {/* Image URL is generated by our public front-image endpoint. */}
                    {/* Every block is the same size; the card's own focal point decides what
                        survives the crop, unless it opted out of cropping altogether. */}
                    <img
                      src={`${card.photoUrl}?w=640`}
                      srcSet={photoSrcSet(card.photoUrl)}
                      // Three across at full width, two on a tablet, one on a phone.
                      sizes="(max-width: 620px) calc(100vw - 32px), (max-width: 900px) calc(50vw - 36px), 380px"
                      loading="lazy"
                      decoding="async"
                      alt={`Postcard from ${card.originCountry}${card.originRegion ? `, ${card.originRegion}` : ""}`}
                      style={{ objectFit: card.showWholeCard ? "contain" : "cover", objectPosition: `${card.focalX}% ${card.focalY}%` }}
                    />
                    {days !== null && <span className="journey-badge">{days} day{days === 1 ? "" : "s"} traveling</span>}
                  </button>
                  {/* A sibling of the photo rather than a child of it, the way the favorite
                      star is: the photo is itself a button, and a button cannot hold one.
                      The number is drawn alone — the sender's name is already the largest
                      thing on the card, and repeating it inside a circle this size was what
                      made a seventeen-letter name unreadable. */}
                  {run && (
                    <button
                      className="sender-stamp"
                      // A second tap shows everyone again, the same as a tap on the name (#74).
                      onClick={() => setSenderFilter((current) => toggledSender(current, card.senderUser))}
                      aria-pressed={filteredToSender}
                      // The ordinal is the whole point of the stamp, and an aria-label beats
                      // both the title and the text in the accessible name — so a label that
                      // only named the action announced everything except which card this is.
                      // It promises no number, either: the tap sets the sender and leaves the
                      // search box and the other filters standing, so "all 4" is a count this
                      // button cannot yet keep.
                      aria-label={`${ordinal(run.nth)} of ${run.total} from ${card.senderUser}. Show this sender's postcards`}
                      title={`${ordinal(run.nth)} of ${run.total} from ${card.senderUser}`}
                    >{ordinal(run.nth)}</button>
                  )}
                  {isOwner ? (
                    <button className={`favorite-star${card.isFavorite ? " is-favorite" : ""}`} onClick={() => toggleFavorite(card)} aria-label={card.isFavorite ? `Remove ${card.senderUser}'s postcard from favorites` : `Favorite ${card.senderUser}'s postcard`} aria-pressed={card.isFavorite} title={card.isFavorite ? "Remove favorite" : "Add favorite"}>{card.isFavorite ? "★" : "☆"}</button>
                  ) : card.isFavorite ? <span className="favorite-star is-favorite public-favorite" title="Favorite">★</span> : null}
                  <div className="card-body">
                    <div className="card-kicker"><span>{card.originCountry}</span><span>{formatDate(card.receivedDate)}</span></div>
                    {/* The name filters to that sender, the way a tag filters to that tag, so
                        a visitor who has never used the search box can still pick out one
                        person's cards (#74). Like the postmark, it leaves the search box and
                        the other filters standing. A card with no name has nobody to filter to.
                        A title, not an aria-label: the button is the heading's whole content,
                        and a label would rename the heading "Show postcards from …". */}
                    <h3>{card.senderUser.trim() ? (
                      <button
                        type="button"
                        className="sender-name"
                        onClick={() => setSenderFilter((current) => toggledSender(current, card.senderUser))}
                        aria-pressed={filteredToSender}
                        title={`Show postcards from ${card.senderUser}`}
                      >{card.senderUser}</button>
                    ) : card.senderUser}</h3>
                    {card.originRegion && <p className="region">{card.originRegion}</p>}
                    {card.acquisitionType && <button className="source-tag" onClick={() => setAcquisitionFilter(card.acquisitionType)}>{acquisitionLabel(card)}</button>}
                    {card.subjectTags.length > 0 && <div className="tags" aria-label="Subject tags">{card.subjectTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className={query.trim().toLowerCase() === tag.toLowerCase() ? "selected" : ""}
                        onClick={() => setQuery((current) => current.trim().toLowerCase() === tag.toLowerCase() ? "" : tag)}
                        aria-pressed={query.trim().toLowerCase() === tag.toLowerCase()}
                        aria-label={`Show postcards tagged ${tag}`}
                      >{tag}</button>
                    ))}</div>}
                    {card.collectionName && (
                      <button className="collection-line" onClick={() => setCollectionFilter(card.collectionName)}>
                        <span>{card.collectionName}</span>
                        <strong>{progress?.owned}{progress?.total ? ` / ${progress.total}` : ""}</strong>
                      </button>
                    )}
                    {/* Owner only, and only when there is something to show: an unrated card
                        with no tags and no thread looks exactly as it always has. */}
                    {isOwner && (hasRatingDetails(card.ownerRating) || hasThreadDetails(card.ownerThread)) && (
                      <div className="card-private">
                        <div className="card-private-top"><OnlyYou /><RatingStars rating={card.ownerRating?.rating ?? null} /></div>
                        {hasThreadDetails(card.ownerThread) && <ThreadLine thread={card.ownerThread} />}
                        {card.ownerRating && card.ownerRating.tags.length > 0 && <TagChips tags={card.ownerRating.tags} />}
                      </div>
                    )}
                    {isOwner && <div className="card-actions">
                      <button onClick={() => openEditForm(card)}>Edit</button>
                      <button className="danger-link" onClick={() => handleDelete(card)}>Remove</button>
                    </div>}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer><span>My Collection of Connection</span><p>Curated by Josephine Stockton · front images only</p></footer>

      {notice && <button className="toast" onClick={() => setNotice("")} aria-live="polite">{notice}<span>×</span></button>}

      {taggingCard && isOwner && (
        <div className="modal-backdrop" role="presentation">
          <section className="tag-review" role="dialog" aria-modal="true" aria-labelledby="tag-review-title">
            <div className="tag-review-photo">
              <img src={`${taggingCard.photoUrl}?w=1024`} alt={`Postcard from ${taggingCard.originCountry}`} />
            </div>
            <form className="tag-review-panel" onSubmit={saveReviewTags}>
              <div className="modal-header">
                <div><p className="eyebrow">One card at a time</p><h2 id="tag-review-title">What is on this postcard?</h2></div>
                <button className="close-button" type="button" onClick={() => setTaggingCardId("")} aria-label="Close">×</button>
              </div>
              <p className="review-progress">{untaggedCards.length} untagged remaining · from {taggingCard.senderUser} in {taggingCard.originCountry}</p>
              <div className="quick-tags" aria-label="Suggested subject tags">
                {allSuggestedTags.map((tag) => (
                  <button key={tag} type="button" className={reviewTags.includes(tag) ? "selected" : ""} onClick={() => toggleReviewTag(tag)} aria-pressed={reviewTags.includes(tag)}>{tag}</button>
                ))}
              </div>
              <label className="custom-tag-field"><span>Anything else?</span><input value={customTags} onChange={(event) => setCustomTags(event.target.value)} placeholder="museum, moon, Colorado…" /></label>
              <div className="modal-actions">
                <button type="button" className="secondary-button" onClick={advanceTagReview}>Skip for now</button>
                <button type="submit" className="primary-button" disabled={savingTags}>{savingTags ? "Saving…" : "Save & next"}</button>
              </div>
            </form>
          </section>
        </div>
      )}

      {showForm && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeForm()}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="form-title">
            <div className="modal-header">
              <div><p className="eyebrow">{editing?.isDraft ? `${drafts.length} new postcard${drafts.length === 1 ? "" : "s"} left` : "Collection record"}</p><h2 id="form-title">{editing?.isDraft ? "Complete new postcard" : editing ? "Edit postcard" : "Add a postcard"}</h2></div>
              <button className="close-button" onClick={closeForm} aria-label="Close">×</button>
            </div>
            {/* Keyed by card so "Save & next" remounts the form: most fields are
                uncontrolled defaultValue inputs, and without the remount they keep the
                previous draft's typed values when the queue advances. */}
            <form key={editing?.id ?? "new"} onSubmit={handleSubmit}>
              <div className="photo-field">
                {photoPreview ? (
                  <div className="focus-picker">
                    <button
                      type="button"
                      className="focus-target"
                      onClick={pickFocalPoint}
                      onKeyDown={nudgeFocalPoint}
                      disabled={showWholeCard}
                      aria-label="Tap the part of the postcard that should stay in view"
                    >
                      <img src={photoPreview} alt="Selected postcard preview" />
                      {!showWholeCard && <span className="focus-dot" style={{ left: `${focalX}%`, top: `${focalY}%` }} aria-hidden="true" />}
                    </button>
                    <div className="focus-side">
                      <p className="eyebrow">In the collection</p>
                      <div className="focus-frame">
                        <img
                          src={photoPreview}
                          alt=""
                          style={{ objectFit: showWholeCard ? "contain" : "cover", objectPosition: `${focalX}% ${focalY}%` }}
                        />
                      </div>
                      <label className="focus-whole">
                        <input name="showWholeCard" type="checkbox" checked={showWholeCard} onChange={(event) => setShowWholeCard(event.target.checked)} />
                        <span>Show the whole card</span>
                      </label>
                      <p className="focus-hint">
                        {showWholeCard
                          ? "The whole postcard fits inside the block. Every block stays the same size."
                          : "Tap the photo to choose what stays in view when the card is cropped."}
                      </p>
                    </div>
                  </div>
                ) : <span>Photo preview</span>}
                <input type="hidden" name="focalX" value={focalX} />
                <input type="hidden" name="focalY" value={focalY} />
                <label className="upload-button">Choose photo<input name="photo" type="file" accept="image/*" required={!editing} onChange={(event) => handlePhotoChange(event.target)} /></label>
              </div>
              <div className="form-grid">
                <label><span>Sender username *</span><input name="senderUser" required defaultValue={editing?.senderUser} placeholder="@username" /></label>
                <label><span>Country *</span><input name="originCountry" required defaultValue={editing?.originCountry} placeholder="Japan" list="known-countries" autoComplete="off" /></label>
                <datalist id="known-countries">{knownCountries.map((name) => <option key={name} value={name} />)}</datalist>
                <label><span>State, province, or region</span><input name="originRegion" defaultValue={editing?.originRegion} placeholder="Hokkaido" /></label>
                <label><span>Subjects</span><input name="subjectTags" value={formSubjectTags} onChange={(event) => setFormSubjectTags(event.target.value)} placeholder="cat, space, illustrated" /></label>
                <div className="quick-tags full-width" aria-label="Suggested subject tags">
                  {allSuggestedTags.map((tag) => {
                    const selected = formSubjectTags.split(",").map((item) => item.trim().toLowerCase()).filter(Boolean).includes(tag);
                    return <button key={tag} type="button" className={selected ? "selected" : ""} onClick={() => toggleFormSubjectTag(tag)} aria-pressed={selected}>{tag}</button>;
                  })}
                </div>
                <label><span>How I got it</span><select name="acquisitionType" value={formAcquisitionType} onChange={(event) => setFormAcquisitionType(event.target.value)}>
                  <option value="">Choose one</option>
                  {Object.entries(acquisitionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></label>
                <label><span>Postcrossing forum type</span><select name="acquisitionDetail" value={formAcquisitionDetail} onChange={(event) => setFormAcquisitionDetail(event.target.value)}>
                  <option value="">Not applicable</option>
                  {Object.entries(acquisitionDetailLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></label>
                {/* Only when the card came with its thread (or is new), like the rating: with
                    no forumThread field at all the server keeps what the card has. */}
                {(!editing || editing.ownerThread) && (formAcquisitionType === FORUM ? (
                  <fieldset className="form-thread full-width">
                    <legend className="form-thread-head"><span>Forum thread (optional)</span><OnlyYou /></legend>
                    <div className="form-thread-grid">
                      <label>
                        <span>Thread</span>
                        <input name={THREAD_FIELD_NAMES.thread} value={formThread.thread} onChange={changeThreadName} onBlur={(event) => fillFromThread(event.target.value)}
                          placeholder="WBMP RR" list="known-threads" autoComplete="off" maxLength={120} />
                      </label>
                      <datalist id="known-threads">{knownThreads.map((option) => <option key={option.key} value={option.label} />)}</datalist>
                      <label>
                        <span>Group</span>
                        <input name={THREAD_FIELD_NAMES.group} value={formThread.group} onChange={(event) => setFormThread((current) => ({ ...current, group: event.target.value }))}
                          placeholder="356" autoComplete="off" maxLength={40} />
                      </label>
                      <label>
                        <span>Topic</span>
                        <input name={THREAD_FIELD_NAMES.topic} value={formThread.topic} onChange={(event) => setFormThread((current) => ({ ...current, topic: event.target.value }))}
                          placeholder="Decorated" autoComplete="off" maxLength={80} />
                      </label>
                      {formAcquisitionDetail === ROUND_ROBIN ? (
                        <label>
                          <span>Moderator</span>
                          <input name={THREAD_FIELD_NAMES.moderator} value={formThread.moderator} onChange={(event) => setFormThread((current) => ({ ...current, moderator: event.target.value }))}
                            placeholder="Postcrossing username" list="known-moderators" autoComplete="off" spellCheck={false} maxLength={60} />
                        </label>
                      ) : <input type="hidden" name={THREAD_FIELD_NAMES.moderator} value="" />}
                      <datalist id="known-moderators">{knownModerators.map((option) => <option key={option.key} value={option.label} />)}</datalist>
                      <label className="full-width">
                        <span>Thread link</span>
                        <input name={THREAD_FIELD_NAMES.link} type="url" inputMode="url" value={formThread.link} onChange={(event) => setFormThread((current) => ({ ...current, link: event.target.value }))}
                          placeholder="https://community.postcrossing.com/t/…" autoComplete="off" spellCheck={false} maxLength={1000} />
                      </label>
                    </div>
                    {threadFilledFrom && <p className="form-thread-note" aria-live="polite">Filled in from your last {threadFilledFrom} card. Change anything that's different.</p>}
                  </fieldset>
                ) : (
                  // Not a forum card, so no thread: the hidden fields are saved empty rather
                  // than kept out of sight on a card that no longer shows them.
                  Object.values(THREAD_FIELD_NAMES).map((name) => <input key={name} type="hidden" name={name} value="" />)
                ))}
                <DateField label="Sent date or month" pickLabel="Pick the sent date" name="sentDate" value={sentDate} onChange={setSentDate} />
                <DateField label="Received date or month" pickLabel="Pick the received date" name="receivedDate" value={receivedDate} onChange={setReceivedDate} />
                <label><span>Collection or box set</span><input name="collectionName" defaultValue={editing?.collectionName} placeholder="Space Cats" /></label>
                <label><span>Total cards in set</span><input name="collectionTotal" type="number" min="1" step="1" defaultValue={editing?.collectionTotal ?? ""} placeholder="12" /></label>
                <label><span>Year printed</span><input name="yearPrinted" type="number" min="1800" max="2200" defaultValue={editing?.yearPrinted ?? ""} /></label>
                <label className="favorite-check"><input name="isFavorite" type="checkbox" defaultChecked={editing?.isFavorite} /><span>Mark as a favorite</span></label>
                <label className="full-width"><span>Message or notes</span><textarea name="message" rows={3} defaultValue={editing?.message} placeholder="A note about the card or its message…" /></label>
                {/* Only when the card came with its rating (or is new). Leaving the fields out
                    entirely tells the server to keep what it has, so an edit can never wipe a
                    rating the page did not load. */}
                {(!editing || editing.ownerRating) && (
                  <div className="form-rating full-width">
                    <div className="form-rating-head"><span>Rating (optional)</span><OnlyYou /></div>
                    <div className="form-rating-row">
                      <StarPicker value={formRating} onPick={setFormRating} />
                      {formRating !== null && (
                        <button type="button" className="rating-clear" onClick={() => setFormRating(null)}>Clear rating</button>
                      )}
                    </div>
                    <input type="hidden" name="rating" value={formRating ?? ""} />
                    {formRatingTags.map((key) => <input key={key} type="hidden" name="ratingTags" value={key} />)}
                    <button type="button" className="rating-tags-toggle" aria-expanded={ratingTagsOpen} onClick={() => setRatingTagsOpen((open) => !open)}>
                      {ratingTagsOpen ? "− Hide tags" : "+ Add tags (optional)"}
                    </button>
                    {ratingTagsOpen && <TagPicker selected={formRatingTags} onToggle={toggleFormRatingTag} />}
                  </div>
                )}
              </div>
              <div className="modal-actions"><button type="button" className="secondary-button" onClick={closeForm}>{editing?.isDraft ? "Finish later" : "Cancel"}</button><button className="primary-button" type="submit" disabled={saving}>{saving ? "Saving…" : editing?.isDraft ? "Save & next" : editing ? "Save changes" : "Add to collection"}</button></div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
